import { Pencil, Trash2, X } from 'lucide-react';
import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface SessionModalProps {
	isOpen: boolean;
	onClose: () => void;
	children: ReactNode;
	/** Sessão P2P agendada pelo profissional: exibe excluir no header. */
	showDeleteAction?: boolean;
	onDelete?: () => void;
	showEditAction?: boolean;
	onEdit?: () => void;
}

const SessionModalHeaderActions = ({
	onClose,
	showDeleteAction,
	onDelete,
	showEditAction,
	onEdit,
}: {
	onClose: () => void;
	showDeleteAction: boolean;
	onDelete?: () => void;
	showEditAction: boolean;
	onEdit?: () => void;
}) => (
	<div className="flex items-center justify-end gap-2 border-b border-stone-200 bg-stone-50 px-3 py-2">
		{showEditAction && onEdit && (
			<button
				type="button"
				onClick={onEdit}
				className="rounded-full p-2 text-stone-500 transition-all hover:bg-stone-200 hover:text-brand-purple"
				title="Editar consulta"
				aria-label="Editar consulta"
			>
				<Pencil size={19} strokeWidth={1.5} />
			</button>
		)}
		{showDeleteAction && onDelete && (
			<button
				type="button"
				onClick={onDelete}
				className="rounded-full p-2 text-stone-500 transition-all hover:bg-stone-200 hover:text-rose-600"
				title="Cancelar consulta"
				aria-label="Cancelar consulta"
			>
				<Trash2 size={19} strokeWidth={1.5} />
			</button>
		)}
		{(showEditAction || showDeleteAction) && (
			<div className="mx-2 h-6 w-px bg-stone-200" aria-hidden />
		)}
		<button
			type="button"
			onClick={onClose}
			className="rounded-full p-2 text-stone-500 transition-all hover:bg-stone-200"
			aria-label="Fechar"
		>
			<X size={22} strokeWidth={1.5} />
		</button>
	</div>
);

export const SessionModal = ({
	isOpen,
	onClose,
	children,
	showDeleteAction = false,
	onDelete,
	showEditAction = false,
	onEdit,
}: SessionModalProps) => {
	useEffect(() => {
		if (isOpen) document.body.style.overflow = 'hidden';
		else document.body.style.overflow = 'auto';
		return () => {
			document.body.style.overflow = 'auto';
		};
	}, [isOpen]);

	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[10000]">
			<div
				className="fixed inset-0 bg-black/60 transition-opacity"
				onClick={onClose}
			/>

			<div className="hidden min-h-screen items-center justify-center px-4 py-10 md:flex lg:py-12">
				<div
					className="relative w-full max-w-[700px] overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-2xl"
					onClick={(e) => e.stopPropagation()}
				>
					<SessionModalHeaderActions
						onClose={onClose}
						showDeleteAction={showDeleteAction}
						onDelete={onDelete}
						showEditAction={showEditAction}
						onEdit={onEdit}
					/>

					<div className="max-h-[calc(100vh-96px)] overflow-y-auto px-6 pb-8 pt-6 text-stone-900">
						{children}
					</div>
				</div>
			</div>

			<div className="fixed inset-x-0 bottom-0 z-50 overflow-hidden rounded-t-2xl border border-stone-200 bg-white shadow-2xl md:hidden">
				<SessionModalHeaderActions
					onClose={onClose}
					showDeleteAction={showDeleteAction}
					onDelete={onDelete}
					showEditAction={showEditAction}
					onEdit={onEdit}
				/>

				<div className="max-h-[calc(100vh-128px)] overflow-y-auto px-4 pb-4 pt-6 text-stone-900">
					{children}
				</div>
			</div>
		</div>,
		document.body,
	);
};
